import React from "react";
import { createColumnHelper } from "@tanstack/react-table";
import StatusPill from "ui-components/status-pill/statusPill";
import TimestampDisplay from "ui-components/timestamp-display/timestampDisplay";
import DockUserCard from "ui-components/dock-user-card/dockUserCard";
import EquipmentCard from "ui-components/equipment-card/equipmentCard";
import XGSNavLink from "ui-components/xgs-nav-link/xgsNavLink";
import {
  formatDoorDisplay,
  formatFreightBarcode,
  humanizeEventType,
  humanizeScanMode,
  scanEventPillVariant,
} from "app/data/dock-scan/dockLogMappers";
import { ScanLog } from "app/data/dock-scan/models";
import { DataTableColumnOption } from "ui-components/data-table/dataTableTypes";
import ScanResultTag from "./components/ScanResultTag";

const columnHelper = createColumnHelper<ScanLog>();

export const DOCK_SCAN_LOGS_LIST_COLUMN_OPTIONS: DataTableColumnOption[] = [
  { id: "time", label: "Time" },
  { id: "employeeId", label: "Employee" },
  { id: "equipmentId", label: "Equipment" },
  { id: "barcodeValue", label: "Barcode" },
  { id: "barcodeType", label: "Barcode Type" },
  { id: "probillNumber", label: "Probill" },
  { id: "rollNumber", label: "Roll" },
  { id: "eventType", label: "Event" },
  { id: "scanMode", label: "Scan Mode" },
  { id: "resultStatus", label: "Result" },
  { id: "resultCode", label: "Result Code" },
  { id: "resultMessage", label: "Message" },
  { id: "door", label: "Door" },
  { id: "trailer", label: "Trailer" },
  { id: "manifest", label: "Manifest" },
  { id: "manifestType", label: "Manifest Type" },
  { id: "manifestId", label: "Manifest ID" },
  { id: "picklistNumber", label: "Picklist" },
  { id: "picklistId", label: "Picklist ID" },
  { id: "workflowType", label: "Workflow" },
  { id: "workflowContextId", label: "Workflow Context" },
  { id: "expectedNextScanType", label: "Expected Next Scan" },
  { id: "recommendedLocationSource", label: "Location Source" },
  { id: "terminalId", label: "Terminal ID" },
  { id: "ms", label: "MS" },
  { id: "scanRequestId", label: "Scan Request ID" },
  { id: "correlationId", label: "Correlation ID" },
  { id: "scannedAt", label: "Scanned At" },
  { id: "createdAt", label: "Created At" },
  { id: "id", label: "ID" },
];

export const DOCK_SCAN_LOGS_LIST_COLUMNS_HIDDEN_BY_DEFAULT: string[] = [
  "barcodeType",
  "resultCode",
  "resultMessage",
  "trailer",
  "manifestType",
  "manifestId",
  "picklistNumber",
  "picklistId",
  "workflowType",
  "workflowContextId",
  "expectedNextScanType",
  "recommendedLocationSource",
  "terminalId",
  "scanRequestId",
  "correlationId",
  "scannedAt",
  "createdAt",
  "id",
];

function formatOptional(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
}

export function getDockScanLogsListColumns(options?: { showEmployee?: boolean }) {
  const showEmployee = options?.showEmployee !== false;

  const cols = [
    columnHelper.accessor((row) => row.scannedAt || row.createdAt, {
      id: "time",
      header: "Time",
      cell: (props) => (
        <TimestampDisplay value={props.getValue()} format="local" />
      ),
      size: 140,
      minSize: 120,
    }),
    columnHelper.accessor("employeeId", {
      id: "employeeId",
      header: "Employee",
      cell: (props) => {
        const row = props.row.original;
        return (
          <DockUserCard
            employeeId={props.getValue()}
            firstName={row.employee?.firstName}
            lastName={row.employee?.lastName}
            keepTooltipInside={false}
          />
        );
      },
      size: 180,
      minSize: 160,
    }),
    columnHelper.accessor("equipmentId", {
      id: "equipmentId",
      header: "Equipment",
      cell: (props) => {
        const id = props.getValue();
        if (!id) return "—";
        return <EquipmentCard unitNumber={id} keepTooltipInside={false} />;
      },
      size: 120,
      minSize: 100,
    }),
    columnHelper.accessor("barcodeValue", {
      id: "barcodeValue",
      header: "Barcode",
      cell: (props) => (
        <div className="dock-scan-logs__barcode-cell">
          <code className="dock-users__mono">{formatFreightBarcode(props.getValue())}</code>
          <span className="dock-scan-logs__barcode-type text-light">
            {(props.row.original.barcodeType || "UNKNOWN").replace(/_/g, " ")}
          </span>
        </div>
      ),
      size: 160,
      minSize: 140,
    }),
    columnHelper.accessor("barcodeType", {
      id: "barcodeType",
      header: "Barcode Type",
      cell: (props) => (props.getValue() || "UNKNOWN").replace(/_/g, " "),
      size: 120,
      minSize: 100,
    }),
    columnHelper.accessor("probillNumber", {
      id: "probillNumber",
      header: "Probill",
      cell: (props) => {
        const value = props.getValue();
        if (value === null || value === undefined || value === "") return "—";
        return (
          <XGSNavLink
            className="xgs-table-link-cell"
            target="_blank"
            rel="noopener noreferrer"
            to={`/shipments/${value}`}
            onClick={(e) => e.stopPropagation()}
          >
            {String(value)}
          </XGSNavLink>
        );
      },
      size: 100,
      minSize: 90,
    }),
    columnHelper.accessor("rollNumber", {
      id: "rollNumber",
      header: "Roll",
      cell: (props) => formatOptional(props.getValue()),
      size: 100,
      minSize: 90,
    }),
    columnHelper.accessor("eventType", {
      id: "eventType",
      header: "Event",
      cell: (props) => (
        <StatusPill
          label={humanizeEventType(props.getValue())}
          variant={scanEventPillVariant(props.getValue(), props.row.original.resultStatus)}
        />
      ),
      size: 140,
      minSize: 120,
    }),
    columnHelper.accessor("scanMode", {
      id: "scanMode",
      header: "Scan Mode",
      cell: (props) => humanizeScanMode(props.getValue()),
      size: 160,
      minSize: 140,
    }),
    columnHelper.accessor("resultStatus", {
      id: "resultStatus",
      header: "Result",
      cell: (props) => <ScanResultTag status={props.getValue()} />,
      size: 120,
      minSize: 110,
    }),
    columnHelper.accessor("resultCode", {
      id: "resultCode",
      header: "Result Code",
      cell: (props) => props.getValue() || "—",
      size: 120,
      minSize: 100,
    }),
    columnHelper.accessor("resultMessage", {
      id: "resultMessage",
      header: "Message",
      cell: (props) => props.getValue() || "—",
      size: 200,
      minSize: 160,
    }),
    columnHelper.accessor("recommendedDoorId", {
      id: "door",
      header: "Door",
      cell: (props) => formatDoorDisplay(props.getValue()) || "—",
      size: 90,
      minSize: 80,
    }),
    columnHelper.accessor("recommendedTrailerId", {
      id: "trailer",
      header: "Trailer",
      cell: (props) => props.getValue() || "—",
      size: 90,
      minSize: 80,
    }),
    columnHelper.accessor("manifestNumber", {
      id: "manifest",
      header: "Manifest",
      cell: (props) => formatOptional(props.getValue()),
      size: 100,
      minSize: 90,
    }),
    columnHelper.accessor("manifestType", {
      id: "manifestType",
      header: "Manifest Type",
      cell: (props) => props.getValue()?.replace(/_/g, " ") || "—",
      size: 120,
      minSize: 100,
    }),
    columnHelper.accessor("manifestId", {
      id: "manifestId",
      header: "Manifest ID",
      cell: (props) => props.getValue() || "—",
      size: 140,
      minSize: 120,
    }),
    columnHelper.accessor("picklistNumber", {
      id: "picklistNumber",
      header: "Picklist",
      cell: (props) => formatOptional(props.getValue()),
      size: 100,
      minSize: 90,
    }),
    columnHelper.accessor("picklistId", {
      id: "picklistId",
      header: "Picklist ID",
      cell: (props) => props.getValue() || "—",
      size: 140,
      minSize: 120,
    }),
    columnHelper.accessor("workflowType", {
      id: "workflowType",
      header: "Workflow",
      cell: (props) => props.getValue()?.replace(/_/g, " ") || "—",
      size: 140,
      minSize: 120,
    }),
    columnHelper.accessor("workflowContextId", {
      id: "workflowContextId",
      header: "Workflow Context",
      cell: (props) => props.getValue() || "—",
      size: 140,
      minSize: 120,
    }),
    columnHelper.accessor("expectedNextScanType", {
      id: "expectedNextScanType",
      header: "Expected Next Scan",
      cell: (props) => props.getValue() || "—",
      size: 140,
      minSize: 120,
    }),
    columnHelper.accessor("recommendedLocationSource", {
      id: "recommendedLocationSource",
      header: "Location Source",
      cell: (props) => props.getValue() || "—",
      size: 130,
      minSize: 110,
    }),
    columnHelper.accessor("terminalId", {
      id: "terminalId",
      header: "Terminal ID",
      cell: (props) => props.getValue() || "—",
      size: 120,
      minSize: 100,
    }),
    columnHelper.accessor("processingDurationMs", {
      id: "ms",
      header: "MS",
      cell: (props) => formatOptional(props.getValue()),
      size: 70,
      minSize: 60,
    }),
    columnHelper.accessor("scanRequestId", {
      id: "scanRequestId",
      header: "Scan Request ID",
      cell: (props) => props.getValue() || "—",
      size: 160,
      minSize: 140,
    }),
    columnHelper.accessor("correlationId", {
      id: "correlationId",
      header: "Correlation ID",
      cell: (props) => props.getValue() || "—",
      size: 160,
      minSize: 140,
    }),
    columnHelper.accessor("scannedAt", {
      id: "scannedAt",
      header: "Scanned At",
      cell: (props) => <TimestampDisplay value={props.getValue()} format="local" />,
      size: 140,
      minSize: 120,
    }),
    columnHelper.accessor("createdAt", {
      id: "createdAt",
      header: "Created At",
      cell: (props) => <TimestampDisplay value={props.getValue()} format="local" />,
      size: 140,
      minSize: 120,
    }),
    columnHelper.accessor("id", {
      id: "id",
      header: "ID",
      cell: (props) => props.getValue() || "—",
      size: 160,
      minSize: 140,
    }),
  ];

  return showEmployee ? cols : cols.filter((col) => col.id !== "employeeId");
}
